<script setup lang="ts">
import { DatePicker } from 'v-calendar';

import Slider from '@vueform/slider';
import '@vueform/slider/themes/default.css';
import { required, min, max, max_value, min_value, email } from '@vee-validate/rules';

import { defineRule, useField, useForm } from 'vee-validate';

// icons
import { faCalendarDay, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { faEraser } from '@fortawesome/pro-light-svg-icons';

// components
import CiSkeletonReviewsCreate from '@/components/skeleton/CiSkeletonReviewsCreate.vue';

// utils
import dayjs from '@/utils/day';
import { COOKIEDOMAIN, RBN_CAMPSITE_DETAIL } from '@/utils/constants';

defineRule('email', email);
defineRule('max', max);
defineRule('max_value', max_value);
defineRule('min', min);
defineRule('min_value', min_value);
defineRule('required', required);
defineRule('rangeOfStay', (value) => {
  if (!value || value.start === null || value.end === null) {
    return false;
  }
  return true;
});

/********************
 * COMPOSITIONS      *
 ********************/
const campsiteStore = useCampsiteStore();
const localePath = useLocalePath();
const reviewService = useReviewService();
const route = useRoute();
const router = useRouter();
const seoStore = useSeoStore();
const userService = useUserService();
const userStore = useUserStore();
const { $gettext } = useGettext();
const { fetchError, fetchFailed, fetchStatus } = useFetchError();
const { getMsgWarning, getMsgError } = useSystemMessages();
const { i18nTermsOfServiceLink, i18nPrivacyPolicyLink } = useI18nMeta();
const { locale } = useI18n();
const { openModal, closeModal } = useCiModal();
const { openModalSuccess, openModalError, openModalMissingPermissions } = useModalDialog({
  campsite: () => campsiteStore.campsite,
});
const {
  idealActivitiesKeys,
  idealActivitiesTranslations,
  ratingDetailsKeys,
  ratingDetailsTranslations,
  suggestedGuestsKeys,
  suggestedGuestsTranslations,
} = useCampsiteRating();
const viewport = useViewport();

/********************
 * REFS & VARS       *
 ********************/
const error = ref<string | null>(null);
const formSubmitted = ref(null);
const rootRef = ref(null);
const selectedGroup = ref(null);
const sliderRefs = reactive<Record<string, InstanceType<typeof Slider>>>({});
const submitPending = ref(false);
const userExists = ref(false);
const userPermissionErrors = ref([]);
const userVerified = ref(false);
const registerAndCreateIsRunning = ref(false);

const step = ref(1);
const stepOne = reactive<Record<string, any>>({
  advantages: '',
  campsite: null,
  comment: '',
  comment_lang_iso2: locale.value,
  date_of_stay: null,
  disadvantages: '',
  email: null,
  first_name: null,
  ideal_activities: [],
  last_name: null,
  rating_calmness: null,
  rating_camper_with_tent_friendly: null,
  rating_catering: null,
  rating_cleanliness_overall: null,
  rating_cleanliness_sanitary: null,
  rating_condition_rentable_accommodations: null,
  rating_couple_friendly: null,
  rating_friendliness: null,
  rating_guests_with_dogs_friendly: null,
  rating_infrastructure: null,
  rating_kid_friendly: null,
  rating_kid_friendly_18: null,
  rating_kid_friendly_6: null,
  rating_leisure: null,
  rating_location: null,
  rating_overall: null,
  rating_price_quality_ratio: null,
  rating_sanitary: null,
  rating_senior_friendly: null,
  rating_youth_friendly: null,
  title: '',
});

const userProfileIncomplete = computed(() => {
  return userPermissionErrors.value.includes('userprofile_incomplete');
});

const i18nCancelText = computed(() => {
  if (step.value === 1) {
    return $gettext('Cancel');
  }
  return $gettext('Back');
});

const i18nSubmitText = computed(() => {
  if (step.value === 1 && userStore.loggedIn) {
    return $gettext('Send');
  }
  if (step.value === 1 && !userStore.loggedIn) {
    return $gettext('Next step');
  }
  if (step.value === 2 && !userExists.value) {
    return $gettext('Submit');
  }
  return '';
});

const i18nDisclaimer = computed(() => {
  if (step.value === 1 && userStore.loggedIn) {
    return $gettext(
      'By submitting a review, you agree to our %{termsLink} and confirm that you have read our %{privacyLink}.',
      { termsLink: i18nTermsOfServiceLink.value, privacyLink: i18nPrivacyPolicyLink.value },
      true,
    );
  }
  return '';
});

const i18nModalBodyText = computed(() => {
  if (userVerified.value) {
    return $gettext('Your review has been saved successfully and is now publicly visible!');
  }

  return $gettext('Your review has been saved successfully');
});

const i18nRatingPolicyLink = computed(() => {
  const text = $gettext('rating policies');
  return `<a href="#policies" class="no-wrap" rel="noopener">${text}</a>`;
});

/********************
 * FORM STEP ONE    *
 ********************/
const stepOneFormFields = computed(() => {
  const fields = [
    {
      label: 'title',
      type: 'text',
      required: true,
      rules: 'required|max:80',
      attributes: {
        maxlength: 80,
      },
    },
    {
      label: 'advantages',
      type: 'textarea',
      required: true,
      rules: 'required',
    },
    {
      label: 'disadvantages',
      type: 'textarea',
      required: true,
      rules: 'required',
    },
  ];

  if (!userStore.loggedIn) {
    const mail = {
      label: 'email',
      type: 'email',
      required: true,
      rules: 'required|email',
    };
    fields.splice(0, 0, mail);
  }

  if (userStore.loggedIn && userProfileIncomplete.value) {
    const incompleteProfile = [
      {
        label: 'first_name',
        type: 'text',
        required: true,
        rules: 'required',
      },
      {
        label: 'last_name',
        type: 'text',
        required: true,
        rules: 'required',
      },
    ];
    fields.splice(0, 0, ...incompleteProfile);
  }

  return fields;
});
const { errors, validate: validateStepOne } = useForm({
  validationSchema: stepOneFormFields.value.reduce(
    (acc, cur) => {
      acc[cur.label] = cur.rules;
      return acc;
    },
    {
      comment: 'required|min:80|max:2000',
      rating_overall: 'required|min_value:1|max_value:5',
      rangeOfStay: 'rangeOfStay',
    },
  ),
});

// dynamically generate field models and push to reactive object
stepOneFormFields.value.forEach((field) => {
  const { value } = useField(field.label, field.rules);
  stepOne[field.label] = value;
});

const { value: commentValue } = useField<string>('comment');
const { value: rangeOfStayValue, errorMessage: rangeOfStayError } = useField<{ start: Date; end: Date }>('rangeOfStay');
const { value: ratingOverallValue } = useField<number>('rating_overall');
stepOne.rating_overall = ratingOverallValue;
stepOne.comment = commentValue;

/********************
 * FORM STEP TWO     *
 * NO LOGIN          *
 ********************/
const {
  values: stepTwoNoLoginValues,
  errors: stepTowNoLoginErrors,
  validate: validateStepTwoNoLogin,
} = useForm({
  validationSchema: {
    stepTwoNoLogin_email: 'required|email',
    stepTwoNoLogin_first_name: 'required',
    stepTwoNoLogin_last_name: 'required',
  },
});
const { value: stepTwoNoLoginEmailValue } = useField<string>('stepTwoNoLogin_email');
const { value: stepTwoNoLoginFirstNameValue } = useField<string>('stepTwoNoLogin_first_name');
const { value: stepTwoNoLoginLastNameValue } = useField<string>('stepTwoNoLogin_last_name');

/********************
 * FORM STEP TWO     *
 * LOGIN             *
 ********************/
const {
  values: stepTwoLoginValues,
  errors: stepTowLoginErrors,
  validate: validateStepTwoLogin,
} = useForm({
  validationSchema: {
    stepTwoLogin_email: 'required|email',
    ...(userProfileIncomplete.value && {
      stepTwoLogin_first_name: 'required',
      stepTwoLogin_last_name: 'required',
    }),
    stepTwoLogin_password: 'required',
  },
});
const { value: stepTwoLoginEmailValue } = useField<string>('stepTwoLogin_email');
const { value: stepTwoLoginFirstNameValue } = useField<string>('stepTwoLogin_first_name');
const { value: stepTwoLoginLastNameValue } = useField<string>('stepTwoLogin_last_name');
const { value: stepTwoLoginPasswordValue } = useField<string>('stepTwoLogin_password');

/********************
 * FORM STEP TWO     *
 * REGISTRATION      *
 ********************/
const {
  values: stepTwoRegistrationValues,
  errors: stepTowRegistrationErrors,
  validate: validateStepTwoRegistration,
} = useForm({
  validationSchema: {
    stepTwoRegistration_email: 'required|email',
    stepTwoRegistration_first_name: 'required',
    stepTwoRegistration_last_name: 'required',
    stepTwoRegistration_password: 'required',
  },
});
const { value: stepTwoRegistrationEmailValue } = useField<string>('stepTwoRegistration_email');
const { value: stepTwoRegistrationFirstNameValue } = useField<string>('stepTwoRegistration_first_name');
const { value: stepTwoRegistrationLastNameValue } = useField<string>('stepTwoRegistration_last_name');
const { value: stepTwoRegistrationPasswordValue } = useField<string>('stepTwoRegistration_password');

/********************
 * INITIALIZATION    *
 ********************/
if (step.value === 1 && route.query.step === '2') {
  await navigateTo({
    name: route.name || undefined,
    params: route.params,
    query: {},
  });
}
const { data, pending, error: dataError } = await useLazyAsyncData(`rate-${route.params.identifier}`, async () => {
  const campsite = campsiteStore.campsite;

  try {
    if (!campsite || campsite.slug !== route.params.identifier) {
      const campsite = await campsiteStore.getCampsite({ slug: route.params.identifier, lang: locale.value });
      seoStore.setDynamicMetaParam({ campsite_name: campsite.meta.name });
      return campsite;
    }
    seoStore.setDynamicMetaParam({ campsite_name: campsite.meta.name });
    return campsite;
  } catch (error) {
    fetchError(error);
    return error;
  }
});

if (dataError.value) {
  throw createError({ statusCode: dataError.value?.statusCode, message: dataError.value.statusMessage });
}

/********************
 * WATCHER           *
 ********************/
watch(
  () => userStore.user.profile?.email,
  (newValue) => {
    if (newValue && !(step.value === 2 && userExists.value)) {
      checkPermissions(newValue);
    }
  },
);
watch(() => data.value, onCampsiteChange, { immediate: true });
watch(() => userPermissionErrors.value, handlePermissionErrors, { immediate: true });
watch(() => stepOne, handleFormChange, { deep: true });
watch(
  [() => stepOne, () => stepTwoNoLoginValues, () => stepTwoLoginValues, () => stepTwoRegistrationValues],
  handleFormChange,
  { deep: true },
);

/********************
 * FUNCTIONS         *
 ********************/
function cancel() {
  if (step.value === 1) {
    router.go(-1);
  } else if (step.value === 2) {
    step.value = 1;
    router.push({
      name: route.name,
      params: route.params,
      query: {},
    });
    nextTick(() => {
      scrollToElement(rootRef.value, 'form');
    });
  }
}

function onCampsiteChange(newValue) {
  if (newValue) {
    stepOne.campsite = newValue.id;
    checkPermissions();
  }
}

function isTouchDevice(): boolean {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

async function checkPermissions(mail = userStore.user.profile?.email || false) {
  if (!mail || registerAndCreateIsRunning.value) {
    return;
  }

  userPermissionErrors.value = [];
  try {
    const response = await userService.checkUser(locale.value, { email: mail, campsite: data.value?.slug, insider_uid: userStore.insiderUid });
    userPermissionErrors.value = response?.permission_errors;
    userExists.value = response?.exists;
    return response;
  } catch (_e) {
    // error is okay
  }
}

function openModalNoDuplicateReviews(errorcode = 'reviews_multiple_per_year_not_allowed') {
  const buttons = [
    {
      label: $gettext('Back to campsite'),
      to: localePath({
        name: RBN_CAMPSITE_DETAIL,
        params: { identifier: campsiteStore.campsite?.slug },
      }),
      variant: 'primary',
    },
  ];

  openModalError(buttons, $gettext('No duplicate review'), getMsgError(errorcode));
}

function onToggleAccordion(idx) {
  selectedGroup.value = selectedGroup.value === idx ? '' : idx;
}

function filterFormData(formData) {
  if (!formData.campsite) {
    formData.campsite = campsiteStore.campsite?.id;
  }
  if (!stepOne.campsite) {
    stepOne.campsite = campsiteStore.campsite?.id;
  }

  const data = {
    marketing_signup: true,
  };
  const keys = Object.keys(formData);
  keys.forEach((key) => {
    if (formData[key] && formData[key] !== 0) {
      data[key] = formData[key];
    }
  });
  return data;
}

function showMailModal(text: string | null = null, headline: string | null = null) {
  const CiModalMail = defineAsyncComponent(() => import('@/components/CiModal/CiModalMail.vue'));

  const buttons = [
    {
      label: $gettext('Back to campsite'),
      to: localePath({
        name: RBN_CAMPSITE_DETAIL,
        params: { identifier: campsiteStore.campsite?.slug },
      }),
      variant: 'primary',
    },
  ];

  openModal(
    {
      component: CiModalMail,
      attrs: {
        name: 'CiModalMail',
        buttons,
        headline: headline || undefined,
        successMessage: text,
        onClose: () => {
          closeModal('CiModalMail');
        },
      },
    },
    {
      clickToClose: false,
      contentTransition: 'translate-y-down',
      contentClass: 'w-full max-w-[800px]',
    },
  );
}

function handlePermissionErrors() {
  /* show missing permission modal */
  if (userPermissionErrors.value.includes('missing_permission_create_campsite_review')) {
    openModalMissingPermissions({ perm: { reason: { code: 'missing_permission_create_campsite_review' } } });
  }

  /* show no duplicate review modal */
  if (userStore.loggedIn && userPermissionErrors.value.includes('reviews_multiple_not_allowed')) {
    openModalNoDuplicateReviews('reviews_multiple_not_allowed');
  }

  /* show no duplicate reviews per year modal */
  if (userStore.loggedIn && userPermissionErrors.value.includes('reviews_multiple_per_year_not_allowed')) {
    openModalNoDuplicateReviews('reviews_multiple_per_year_not_allowed');
  }

  /* show no duplicate error message */
  if (!userStore.loggedIn && userPermissionErrors.value.includes('reviews_multiple_not_allowed')) {
    error.value = getMsgError('reviews_multiple_not_allowed');
  }

  /* show no duplicate error message */
  if (!userStore.loggedIn && userPermissionErrors.value.includes('reviews_multiple_per_year_not_allowed')) {
    error.value = getMsgError('reviews_multiple_per_year_not_allowed');
  }
}

function handleFormError(err) {
  if (err?.response?._data?.code) {
    error.value = getMsgError(err.response._data.code);
  } else if (err?.response?._data?.non_field_errors?.length > 0) {
    error.value = err.response._data.non_field_errors.map(e => e.message).join();
  } else {
    const errKeys = Object.keys(err.response._data);
    let errorMsg = `<p>${$gettext('One or more error(s) occurred:')}</p>`;
    errorMsg += '<ul class="list-none mb-0">';
    errKeys.forEach((key) => {
      errorMsg += `<li><span class="text-medium">${getLabel(key)}: </span>${getMsgError(
        err.response.data[key][0].code,
      )}</li>`;
    });
    errorMsg += '</ul>';
    error.value = errorMsg;
  }
}

function handleFormChange() {
  error.value = null;

  if (stepOne.email) {
    stepOne.email = stepOne.email.trim();
  }

  if (stepTwoLoginEmailValue.value) {
    stepTwoLoginEmailValue.value = stepTwoLoginEmailValue.value.trim();
  }

  if (stepTwoNoLoginEmailValue.value) {
    stepTwoNoLoginEmailValue.value = stepTwoNoLoginEmailValue.value.trim();
  }

  if (stepTwoRegistrationEmailValue.value) {
    stepTwoRegistrationEmailValue.value = stepTwoRegistrationEmailValue.value.trim();
  }
}

// form submit
async function submitStepOne() {
  submitPending.value = true;
  const { valid } = await validateStepOne();

  if (!valid) {
    submitPending.value = false;
    scrollToElement(rootRef.value, 'has-error');
    return false;
  }

  /* get mail for permission check */
  const email = (userStore.loggedIn && userStore.user.profile?.email) || stepOne.email;
  const response = await checkPermissions(email);

  if (response) {
    /* user logged in & no permission errors - send form */
    if (userStore.loggedIn) {
      const data = filterFormData(stepOne);
      reviewService
        .create(locale.value, data)
        .then((response) => {
          const warningKeys = Object.keys(response.meta.warnings ? response.meta.warnings : {});
          const warnings = [];
          warningKeys.forEach((key) => {
            warnings.push(getMsgWarning(key));
          });

          userVerified.value = !warningKeys.includes('email_not_verified');

          const buttons = [
            {
              label: $gettext('Back to campsite'),
              to: localePath({
                name: RBN_CAMPSITE_DETAIL,
                params: { identifier: campsiteStore.campsite?.slug },
              }),
              variant: 'primary',
            },
          ];
          openModalSuccess(buttons, warnings, $gettext('Review successfully saved'), i18nModalBodyText.value);
          // clear campsite data at this point to force refetch on detail page to show update reviews
          campsiteStore.clearCampsite();
          return navigateTo({
            name: route.name || undefined,
            params: route.params,
            query: { step: 3, success: userVerified.value ? 'success' : 'notVerified' },
          });
        })
        .catch((err) => {
          submitPending.value = false;
          if (err.response?._data?.code) {
            error.value = getMsgError(err.response._data.code);
          } else {
            const errKeys = Object.keys(err.response._data);
            let errorMsg = `<p>${$gettext('One or more error(s) occurred:')}</p>`;
            errorMsg += '<ul class="list-none mb-0">';
            errKeys.forEach((key) => {
              errorMsg += `<li><span class="font-medium">${getLabel(key)}: </span>${getMsgError(
                err.response._data[key][0].code,
              )}</li>`;
            });
            errorMsg += '</ul>';
            error.value = errorMsg;
          }
        });
      return;
    }

    if (
      userPermissionErrors.value.includes('reviews_multiple_not_allowed')
      || userPermissionErrors.value.includes('reviews_multiple_per_year_not_allowed')
    ) {
      submitPending.value = false;
      return;
    }

    /* in any other case go to next step  */
    stepTwoLoginEmailValue.value = stepOne.email;
    stepTwoNoLoginEmailValue.value = stepOne.email;
    stepTwoRegistrationEmailValue.value = stepOne.email;

    submitPending.value = false;

    step.value = 2;

    const query = {
      ...route.query,
      step: 2,
      userExists: userExists.value,
    };

    await navigateTo({
      name: route.name || undefined,
      params: route.params,
      query,
    });

    /* scroll to top */
    if (step.value === 2) {
      nextTick(() => {
        scrollToElement(rootRef.value, 'form');
      });
    }
  }
}

async function submitNoLogin() {
  submitPending.value = true;
  const { valid } = await validateStepTwoNoLogin();

  if (!valid) {
    submitPending.value = false;
    scrollToElement(rootRef.value, 'has-error');
    return false;
  }

  const data = filterFormData({
    ...stepOne,
    email: stepTwoNoLoginEmailValue.value,
    first_name: stepTwoNoLoginFirstNameValue.value,
    last_name: stepTwoNoLoginLastNameValue.value,
  });
  reviewService
    .create(locale.value, data)
    .then(() => {
      const headline = $gettext('Only one more step');
      const modalText = $gettext(
        'You will receive an e-mail to confirm your data and to publish the review. Please click on the link in the email to complete the process.',
      );
      showMailModal(modalText, headline);
      submitPending.value = false;
      return navigateTo({
        name: route.name || undefined,
        params: route.params,
        query: { step: 3, success: 'noLogin' },
      });
    })
    .catch((error) => {
      submitPending.value = false;
      handleFormError(error);
    });
}

async function submitLogin() {
  submitPending.value = true;
  const { valid } = await validateStepTwoLogin();

  if (!valid) {
    submitPending.value = false;
    scrollToElement(rootRef.value, 'has-error');
    return false;
  }

  const data = filterFormData({
    ...stepOne,
    email: stepTwoLoginEmailValue.value,
    first_name: stepTwoLoginFirstNameValue.value,
    last_name: stepTwoLoginLastNameValue.value,
    password: stepTwoLoginPasswordValue.value,
    insider_uid: userStore.insiderUid,
  });

  reviewService
    .loginAndCreate(locale.value, data)
    .then(async (res: any) => {
      submitPending.value = false;
      await userStore.saveToken({ token: res.user.key, persistence: false });
      userStore.init({ lang: locale.value });
      if (res?.user?.status?.email_verified) {
        submitPending.value = false;
        return navigateTo(
          localePath({
            name: 'campsite-identifier-rate-confirm',
            params: route.params,
            query: { verified: 1, review: res.id },
          }),
        );
      } else {
        const modalText = $gettext(
          'Your email address has not been verified yet. We have sent you a new verification email. To publish your reviews, pictures and videos you need to verify your account first.',
        );
        showMailModal(modalText);
        submitPending.value = false;
        return navigateTo(
          localePath({
            name: route.name || undefined,
            params: route.params,
            query: { step: 3, success: 'loginNotVerified' },
          }),
        );
      }
    })
    .catch((error) => {
      submitPending.value = false;
      handleFormError(error);
    });
}

async function submitRegistration() {
  submitPending.value = true;
  const { valid } = await validateStepTwoRegistration();

  if (!valid) {
    submitPending.value = false;
    scrollToElement(rootRef.value, 'has-error');
    return false;
  }

  const data = filterFormData({
    ...stepOne,
    email: stepTwoRegistrationEmailValue.value,
    first_name: stepTwoRegistrationFirstNameValue.value,
    last_name: stepTwoRegistrationLastNameValue.value,
    password: stepTwoRegistrationPasswordValue.value,
    insider_uid: userStore.insiderUid || null,
  });

  registerAndCreateIsRunning.value = true;
  reviewService
    .registerAndCreate(locale.value, data)
    .then(async (res: any) => {
      // log user in
      await userStore.saveToken({ token: res.user.key, persistence: false });
      userStore.saveUser({ user: res.user });

      const headline = $gettext('Only one more step');
      const modalText = $gettext(
        'You will receive a confirmation email. Please click on the link in the email to complete your review.',
      );
      showMailModal(modalText, headline);
      submitPending.value = false;

      nextTick(() => {
        registerAndCreateIsRunning.value = false;
      });

      return navigateTo({
        name: route.name,
        params: route.params,
        query: { step: 3, success: 'register' },
      });
    })
    .catch((error) => {
      nextTick(() => {
        registerAndCreateIsRunning.value = false;
      });
      submitPending.value = false;
      handleFormError(error);
    });
}

function getPlaceholder(label: string) {
  let placeholder = '';
  switch (label) {
    case 'title':
      placeholder = $gettext('Headline (max. 80 characters)');
      break;
    case 'advantages':
      placeholder = $gettext('What did you like?');
      break;
    case 'disadvantages':
      placeholder = $gettext('What did you dislike?');
      break;
    case 'comment':
      placeholder = $gettext('Write your comment here (min. 80 characters)');
      break;

    default:
      break;
  }
  return placeholder;
}

function getLabel(label: string) {
  let inputLabel = '';
  switch (label) {
    case 'title':
      inputLabel = $gettext('Title');
      break;
    case 'advantages':
      inputLabel = $gettext('Advantages');
      break;
    case 'disadvantages':
      inputLabel = $gettext('Disadvantages');
      break;
    case 'comment':
      inputLabel = $gettext('Comment');
      break;
    case 'date_of_stay':
      inputLabel = $gettext('Date of stay');
      break;
    case 'rating_overall':
      inputLabel = $gettext('Rating overall');
      break;
    case 'first_name':
      inputLabel = $gettext('Firstname');
      break;
    case 'last_name':
      inputLabel = $gettext('Lastname');
      break;
    case 'email':
      inputLabel = $gettext('E-Mail');
      break;
    case 'password':
      inputLabel = $gettext('Password');
      break;
    default:
      break;
  }
  return inputLabel;
}

function onClickGuidelines(evt) {
  if (evt.target.hash === '#policies') {
    evt.preventDefault();

    const CiReviewModalGuidelines = defineAsyncComponent({
      loader: () => import('@/components/CiReview/CiReviewModalGuidelines.vue'),
    });

    openModal(
      {
        component: CiReviewModalGuidelines,
        attrs: {
          name: 'CiReviewModalGuidelines',
          closeable: true,
          title: $gettext('Guidelines for ratings'),
          onClose: () => {
            closeModal('CiReviewModalGuidelines');
          },
        },
      },
      {
        clickToClose: true,
        contentTransition: 'translate-y-down',
        contentClass: 'max-w-[1140px] w-full h-auto',
      },
    );
  }
}

function onDateSelect(value) {
  if (!value || !value.start) {
    rangeOfStayValue.value = { start: null, end: null };
    return;
  }

  stepOne.date_of_stay = dayjs(value.start).format('YYYY-MM-DD');
}

function onDragStart(_value, name) {
  sliderRefs[name].slider.noUiSlider.updateOptions(
    {
      tooltips: { to: tooltipText },
    },
    false,
  );

  nextTick(() => {
    [...sliderRefs[name].slider.querySelectorAll('.slider-tooltip')].forEach((ele) => {
      ele.classList.remove('slider-tooltip-bottom');
      ele.classList.add('slider-tooltip-top');
    });
  });
}

function onDragEnd(_value, name) {
  nextTick(() => {
    [...sliderRefs[name].slider.querySelectorAll('.slider-tooltip')].forEach((ele) => {
      ele.classList.remove('slider-tooltip-top');
      ele.classList.add('slider-tooltip-bottom');
    });
  });
}

function tooltipText(value) {
  if (value === 0 || value === null) {
    return $gettext('No rating');
  }
  return `${value}`;
}

function onIdealActivitySelect(payload, key) {
  if (payload && stepOne.ideal_activities.length < 5) {
    stepOne.ideal_activities.push(key);
  }

  if (!payload) {
    stepOne.ideal_activities.splice(stepOne.ideal_activities.indexOf(key), 1);
  }
}

async function submitOauth(redirectUrl: string) {
  submitPending.value = true;

  const data = filterFormData({
    ...stepOne,
    email: stepTwoLoginEmailValue.value,
    first_name: stepTwoLoginFirstNameValue.value,
    last_name: stepTwoLoginLastNameValue.value,
    password: stepTwoLoginPasswordValue.value,
  });
  const reviewCreateCookie = useCookie<string | null>('_ci_rc', { sameSite: 'strict', secure: true, ...(COOKIEDOMAIN && { domain: COOKIEDOMAIN }) });
  reviewCreateCookie.value = JSON.stringify(data);
  navigateTo(redirectUrl, { external: true });
}
</script>

<template>
  <div
    v-if="!fetchFailed"
    ref="rootRef"
    class="reviews"
  >
    <div class="bg-gray-10">
      <div class="container py-2">
        <CiNavigationToggle
          type="black"
          :href="localePath({ name: RBN_CAMPSITE_DETAIL, params: { identifier: route.params.identifier } })"
        >
          <span class="ml-4 text-dark">{{ $gettext('Back to campsite') }}</span>
        </CiNavigationToggle>
      </div>
    </div>
    <client-only>
      <div
        v-if="!pending"
        class="container"
      >
        <form novalidate>
          <!-- form step 1 -->
          <div v-if="step === 1">
            <!-- headline -->
            <template
              v-if="campsiteStore.campsite?.meta"
            >
              <h1 class="mb-0 mt-12 text-center lg:text-left">
                {{ $gettext('Rate %{campsiteName}', { campsiteName: campsiteStore.campsite.meta.name }, true) }}
              </h1>
            </template>

            <!-- subline & copy -->
            <h2 class="mt-12">
              {{ $gettext('Your rating') }}
            </h2>
            <span
              @click="onClickGuidelines"
              v-html="
                $gettext(
                  'Please keep your comment about your stay at this campsite factual and honest. Our %{termsLink} and %{ratingPolicyLink} apply.',
                  { termsLink: i18nTermsOfServiceLink, ratingPolicyLink: i18nRatingPolicyLink },
                  true,
                )
              "
            />

            <!-- form input -->
            <div class="mt-12 grid grid-cols-12 md:gap-x-[30px]">
              <div class="col-span-12 lg:col-span-6">
                <template
                  v-for="field in stepOneFormFields"
                  :key="field.name"
                >
                  <InputTextfield
                    :id="field.label"
                    v-bind="field.attributes"
                    v-model="stepOne[field.label]"
                    class="truncate"
                    :data-cy="`reviews-create--input-${field.label}`"
                    :disabled="submitPending"
                    :error="!!errors[field.label]"
                    :label="getLabel(field.label)"
                    :name="field.label"
                    :placeholder="getPlaceholder(field.label)"
                    :required="field.required"
                    :type="field.type"
                    inverted
                  />
                </template>

                <div
                  class="mb-6"
                  :class="{ 'has-error': rangeOfStayError }"
                >
                  <label class="input-label font-medium"><span>{{ $gettext('Date of stay') }}</span><sup>*</sup></label>
                  <div
                    class="input-field input--inverted !p-0"
                    data-cy="reviews-create--dateOfStay"
                  >
                    <DatePicker
                      v-model.range="rangeOfStayValue"
                      :locale="locale"
                      color="green"
                      :initial-page="{ month: new Date().getMonth() + 1, year: new Date().getFullYear() }"
                      :max-date="new Date()"
                      :masks="{ input: 'D. MMMM YYYY' }"
                      :update-on-input="false"
                      :columns="viewport.isLessThan('md') ? 1 : 2"
                      name="rangeOfStay"
                      @update:model-value="onDateSelect"
                    >
                      <template #default="{ inputValue, togglePopover }">
                        <div
                          class="flex size-full flex-row items-center rounded p-4"
                          :class="[
                            submitPending
                              ? 'pointer-events-none cursor-not-allowed bg-gray-30'
                              : 'cursor-pointer bg-gray-10',
                          ]"
                          @click="togglePopover"
                        >
                          <CiAwesomeIcon
                            :icon="faCalendarDay"
                            class=""
                          />
                          <span
                            v-if="inputValue.start && inputValue.end"
                            class="ml-2"
                          >{{ inputValue.start }} - {{ inputValue.end }}</span>
                          <span
                            v-else
                            class="ml-2 text-gray-light"
                          > {{ $gettext('Select date of stay...') }}</span>
                          <CiAwesomeIcon
                            v-if="rangeOfStayValue?.start || rangeOfStayValue?.end"
                            :icon="faTimes"
                            class="ml-auto"
                            @click.stop.prevent="() => onDateSelect(null)"
                          />
                        </div>
                      </template>
                    </DatePicker>
                  </div>
                </div>
              </div>
              <div class="col-span-12 lg:col-span-6">
                <InputTextfield
                  id="comment"
                  v-model="commentValue"
                  :disabled="submitPending"
                  :error="!!errors['comment']"
                  :label="getLabel('comment')"
                  :maxlength="2000"
                  :minlength="80"
                  :placeholder="getPlaceholder('comment')"
                  :required="true"
                  class="h-full"
                  data-cy="reviews-create--input-comment"
                  inverted
                  name="comment"
                  type="textarea"
                />
              </div>

              <!-- form slider overall -->
              <div
                :class="{ 'has-error': !!errors['rating_overall'] }"
                class="col-span-12 mt-12"
              >
                <h3 class="input-label">
                  {{ getLabel('rating_overall') }}<sup>*</sup>
                </h3>
              </div>
              <div class="col-span-12 mb-6">
                <div class="px-4 md:px-3">
                  <Slider
                    id="slider_rating_overall"
                    :ref="el => (sliderRefs['rating_overall'] = el as InstanceType<typeof Slider>)"
                    v-model="stepOne['rating_overall']"
                    class="mb-2 mt-4"
                    :disabled="submitPending"
                    :max="5"
                    :min="0"
                    :steps="1"
                    name="rating_overall"
                    :format="tooltipText"
                    :options="{
                      start: stepOne['rating_overall'] || 0,
                      behaviour: isTouchDevice() ? 'drag' : 'tap',
                    }"
                    :tooltips="stepOne['rating_overall'] > 0"
                    :tooltip-position="'bottom'"
                    :lazy="true"
                    data-cy="reviews-create--rating_overall"
                    @start="value => onDragStart(value, 'rating_overall')"
                    @end="value => onDragEnd(value, 'rating_overall')"
                  />

                  <div class="mt-2 flex justify-between text-sm">
                    <div>{{ $gettext('No rating') }}</div>
                    <div>{{ $gettext('Very good') }}</div>
                  </div>
                </div>
              </div>

              <!-- form slider detail -->
              <div class="col-span-12 mt-12">
                <h3>{{ $gettext('Detailed rating concerning…') }}</h3>
              </div>

              <div
                v-for="(key, index) in ratingDetailsKeys"
                :key="key"
                :class="{ 'mt-12': index !== 0 && index !== 1, 'mt-6': index === 0 || index === 1 }"
                class="col-span-12 lg:col-span-6"
              >
                <div class="flex">
                  <span class="font-medium">
                    {{ ratingDetailsTranslations[key] }}
                  </span>
                  <CiAwesomeIcon
                    v-if="stepOne[key]"
                    :icon="faEraser"
                    class="icon ml-auto cursor-pointer self-end"
                    ratio="0.75"
                    @click="sliderRefs[key].slider.noUiSlider.set(0)"
                  />
                </div>
                <div class="px-4 md:px-3">
                  <Slider
                    :id="`slider_${key}`"
                    :ref="el => (sliderRefs[key] = el as InstanceType<typeof Slider>)"
                    v-model="stepOne[key]"
                    :disabled="submitPending"
                    :format="tooltipText"
                    :options="{
                      start: stepOne[key] || 0,
                      behaviour: isTouchDevice() ? 'drag' : 'tap',
                    }"
                    :tooltips="stepOne[key] > 0"
                    :tooltip-position="'bottom'"
                    :lazy="true"
                    :max="5"
                    :min="0"
                    :step="1"
                    class="mb-2 mt-4"
                    @start="value => onDragStart(value, key)"
                    @end="value => onDragEnd(value, key)"
                  />

                  <div class="mt-2 flex justify-between text-sm">
                    <div>{{ $gettext('No rating') }}</div>
                    <div>{{ $gettext('Very good') }}</div>
                  </div>
                </div>
              </div>

              <!-- form slider suitable -->
              <div class="col-span-12 mt-12">
                <h3>{{ $gettext('Suitable for…') }}</h3>
              </div>

              <div
                v-for="(key, index) in suggestedGuestsKeys"
                :key="key"
                :class="{ 'mt-12': index !== 0 && index !== 1, 'mt-6': index === 0 || index === 1 }"
                class="col-span-12 lg:col-span-6"
              >
                <div class="flex">
                  <span class="font-medium">
                    {{ suggestedGuestsTranslations[key] }}
                  </span>
                  <CiAwesomeIcon
                    v-if="stepOne[key]"
                    :icon="faEraser"
                    class="icon ml-auto cursor-pointer self-end"
                    ratio="0.75"
                    @click="sliderRefs[key].slider.noUiSlider.set(0)"
                  />
                </div>
                <div class="px-4 md:px-3">
                  <Slider
                    :id="`slider_${key}`"
                    :ref="el => (sliderRefs[key] = el as InstanceType<typeof Slider>)"
                    v-model="stepOne[key]"
                    :disabled="submitPending"
                    class="mb-2 mt-4"
                    :max="5"
                    :min="0"
                    :step="1"
                    :format="tooltipText"
                    :options="{
                      start: stepOne[key] || 0,
                      behaviour: isTouchDevice() ? 'drag' : 'tap',
                    }"
                    :tooltips="stepOne[key] > 0"
                    :tooltip-position="'bottom'"
                    :lazy="true"
                    @start="value => onDragStart(value, key)"
                    @end="value => onDragEnd(value, key)"
                  />

                  <div class="mt-2 flex justify-between text-sm">
                    <div>{{ $gettext('No rating') }}</div>
                    <div>{{ $gettext('Very good') }}</div>
                  </div>
                </div>
              </div>

              <!-- form ideal -->
              <div class="col-span-12 mb-4 mt-12">
                <h3>{{ $gettext('Ideal for…') }}</h3>
                <span class="small">{{ $gettext('Select a maximum of 5') }}</span>
              </div>

              <div
                v-for="key in idealActivitiesKeys"
                :key="key"
                class="col-span-12 md:col-span-6"
              >
                <CiFilterBoolean
                  :label="idealActivitiesTranslations[key]"
                  :name="key"
                  :value="stepOne.ideal_activities.indexOf(key) !== -1"
                  class="mb-2"
                  @input="onIdealActivitySelect($event, key)"
                />
              </div>
            </div>
          </div>
        </form>

        <!-- form step 2 -->
        <template v-if="step === 2">
          <div class="mt-12">
            <!-- user exists -->
            <template v-if="userExists">
              <div class="mx-auto w-full md:w-8/12 lg:w-1/2">
                <h1>{{ $gettext('Confirm your rating via email or login') }}</h1>
              </div>
              <div class="mx-auto mt-4 w-full md:w-8/12 lg:mt-12 lg:w-1/2">
                <CiAccordion
                  :show="selectedGroup === 1"
                  :mq="'md'"
                  @accordion-toggle="onToggleAccordion(1)"
                >
                  <template #header>
                    <h5 class="m-0 px-0 py-4 md:py-0">
                      {{ $gettext('Confirm your rating by login') }}
                    </h5>
                  </template>
                  <form
                    novalidate
                    @submit="submitLogin"
                  >
                    <div class="small my-4">
                      {{
                        $gettext(
                          'You can confirm your rating quickly and easily via login.',
                        )
                      }}
                    </div>

                    <CiSocialLogin
                      :redirect-to="{
                        name: 'campsite-identifier-rate-confirm-oauth',
                        params: route.params,
                      }"
                      :capture-click="true"
                      @callback="submitOauth"
                    />

                    <InputTextfield
                      id="stepTwoLogin_email"
                      v-model="stepTwoLoginEmailValue"
                      :disabled="submitPending"
                      :error="!!stepTowLoginErrors['stepTwoLogin_email']"
                      :label="getLabel('email')"
                      :placeholder="getPlaceholder('email')"
                      :read-only="true"
                      :required="true"
                      inverted
                      name="stepTwoLogin_email"
                      tabindex="-1"
                      type="email"
                    />
                    <InputTextfield
                      id="stepTwoLogin_password"
                      v-model="stepTwoLoginPasswordValue"
                      :disabled="submitPending"
                      :error="!!stepTowLoginErrors['stepTwoLogin_password']"
                      :label="getLabel('password')"
                      :required="true"
                      inverted
                      name="stepTwoLogin_password"
                      type="password"
                    />
                    <InputTextfield
                      v-if="userProfileIncomplete"
                      id="stepTwoLogin_first_name"
                      v-model="stepTwoLoginFirstNameValue"
                      :disabled="submitPending"
                      :error="!!stepTowLoginErrors['stepTwoLogin_first_name']"
                      :label="getLabel('first_name')"
                      :placeholder="getPlaceholder('first_name')"
                      :required="true"
                      inverted
                      name="stepTwoLogin_first_name"
                      type="text"
                    />
                    <InputTextfield
                      v-if="userProfileIncomplete"
                      id="stepTwoLogin_last_name"
                      v-model="stepTwoLoginLastNameValue"
                      :disabled="submitPending"
                      :error="!!stepTowLoginErrors['stepTwoLogin_last_name']"
                      :label="getLabel('last_name')"
                      :placeholder="getPlaceholder('last_name')"
                      :required="true"
                      inverted
                      name="stepTwoLogin_last_name"
                      type="text"
                    />
                    <div
                      v-if="error && formSubmitted === 'stepTwoLogin'"
                      class="mb-6"
                      data-cy="reviews-create--error-wrapper-stepTwoLogin"
                    >
                      <CiInfoPanel
                        :condition="true"
                        type="danger"
                      >
                        <div v-html="error" />
                      </CiInfoPanel>
                    </div>

                    <div class="small mb-6">
                      <sup>*</sup><span>{{ $gettext('Mandatory fields') }}</span>
                    </div>

                    <div class="mb-12">
                      <div
                        class="mb-2 px-4 text-center text-sm"
                        v-html="
                          $gettext(
                            'By logging in, you agree to our %{termsLink} and confirm that you have read our %{privacyLink}.',
                            { termsLink: i18nTermsOfServiceLink, privacyLink: i18nPrivacyPolicyLink },
                            true,
                          )
                        "
                      />
                      <CiButton
                        tag="button"
                        :disabled="submitPending"
                        class="w-full"
                        @click="submitLogin"
                      >
                        <CiSpinner
                          v-if="submitPending"
                          class="mr-2"
                          type="white"
                          size="sm"
                        />
                        {{ $gettext('Log in and submit review') }}
                      </CiButton>
                      <div class="mt-6">
                        <CiButton
                          tag="button"
                          variant="text"
                          class="w-full"
                          :disabled="submitPending"
                          @click="cancel"
                        >
                          {{ i18nCancelText }}
                        </CiButton>
                      </div>
                    </div>
                  </form>
                </CiAccordion>
                <CiAccordion
                  :mq="'md'"
                  :show="selectedGroup === 0"
                  @accordion-toggle="onToggleAccordion(0)"
                >
                  <template #header>
                    <h5 class="m-0 px-0 py-4 md:py-0">
                      {{ $gettext('Confirm your rating by e-mail') }}
                    </h5>
                  </template>
                  <form
                    novalidate
                    @submit="submitNoLogin"
                  >
                    <div class="small my-4">
                      {{
                        $gettext(
                          'You will receive an e-mail for confirmation. Please click on the link in the email to complete your review.',
                        )
                      }}
                    </div>
                    <InputTextfield
                      id="stepTwoNoLogin_email"
                      v-model="stepTwoNoLoginEmailValue"
                      :disabled="submitPending"
                      :error="!!stepTowNoLoginErrors['stepTwoNoLogin_email']"
                      :label="getLabel('email')"
                      :placeholder="getPlaceholder('email')"
                      :read-only="true"
                      :required="true"
                      inverted
                      name="stepTwoNoLogin_email"
                      tabindex="-1"
                      type="email"
                    />
                    <InputTextfield
                      id="stepTwoNoLogin_first_name"
                      v-model="stepTwoNoLoginFirstNameValue"
                      :disabled="submitPending"
                      :error="!!stepTowNoLoginErrors['stepTwoNoLogin_first_name']"
                      :label="getLabel('first_name')"
                      :placeholder="getPlaceholder('first_name')"
                      :required="true"
                      inverted
                      name="stepTwoNoLogin_first_name"
                      type="text"
                    />
                    <InputTextfield
                      id="stepTwoNoLogin_last_name"
                      v-model="stepTwoNoLoginLastNameValue"
                      :disabled="submitPending"
                      :error="!!stepTowNoLoginErrors['stepTwoNoLogin_last_name']"
                      :label="getLabel('last_name')"
                      :placeholder="getPlaceholder('last_name')"
                      :required="true"
                      inverted
                      name="stepTwoNoLogin_last_name"
                      type="text"
                    />
                    <div class="small mb-6">
                      <sup>*</sup><span>{{ $gettext('Mandatory fields') }}</span>
                    </div>

                    <!-- errors -->
                    <div
                      v-if="error && formSubmitted === 'stepTwoNoLogin'"
                      class="mb-6"
                      data-cy="reviews-create--error-wrapper-stepTwoNoLogin"
                    >
                      <CiInfoPanel
                        :condition="true"
                        type="danger"
                      >
                        <div v-html="error" />
                      </CiInfoPanel>
                    </div>

                    <div class="mb-12">
                      <div
                        class="mb-2 px-4 text-center text-sm"
                        v-html="
                          $gettext(
                            'By continuing, you agree to our %{termsLink} and confirm that you have read our %{privacyLink}.',
                            { termsLink: i18nTermsOfServiceLink, privacyLink: i18nPrivacyPolicyLink },
                            true,
                          )
                        "
                      />

                      <CiButton
                        tag="button"
                        :disabled="submitPending"
                        class="w-full"
                        @click="submitNoLogin"
                      >
                        <CiSpinner
                          v-if="submitPending"
                          class="mr-2"
                          type="white"
                          size="sm"
                        />
                        {{ $gettext('Receive e-mail') }}
                      </CiButton>

                      <div class="mt-6 flex md:hidden">
                        <CiButton
                          tag="button"
                          variant="text"
                          class="w-full"
                          :disabled="submitPending"
                          @click="cancel"
                        >
                          {{ i18nCancelText }}
                        </CiButton>
                      </div>
                    </div>
                  </form>
                </CiAccordion>
              </div>
            </template>
            <!-- user does NOT exists -->
            <template v-else>
              <form
                novalidate
                class="!mx-0 grid grid-cols-2 md:gap-x-[30px]"
                @submit="submitRegistration"
              >
                <div class="col-span-2">
                  <h1>{{ $gettext('Publish your review') }}</h1>
                  <p>{{ $gettext('Please register with your data then your review will be published.') }}</p>
                </div>

                <div class="col-span-2">
                  <div class="mx-auto w-full max-w-[480px]">
                    <CiSocialLogin
                      :redirect-to="{
                        name: 'campsite-identifier-rate-confirm-oauth',
                        params: route.params,
                      }"
                      :capture-click="true"
                      @callback="submitOauth"
                    />
                  </div>
                </div>

                <div class="col-span-2 mt-5 md:col-span-1">
                  <InputTextfield
                    id="stepTwoRegistration_first_name"
                    v-model="stepTwoRegistrationFirstNameValue"
                    :disabled="submitPending"
                    :error="!!stepTowRegistrationErrors['stepTwoRegistration_first_name']"
                    :label="getLabel('first_name')"
                    :placeholder="getPlaceholder('first_name')"
                    :required="true"
                    inverted
                    name="stepTwoRegistration_first_name"
                    type="text"
                  />
                  <InputTextfield
                    id="stepTwoRegistration_last_name"
                    v-model="stepTwoRegistrationLastNameValue"
                    :disabled="submitPending"
                    :error="!!stepTowRegistrationErrors['stepTwoRegistration_last_name']"
                    :label="getLabel('last_name')"
                    :placeholder="getPlaceholder('last_name')"
                    :required="true"
                    inverted
                    name="stepTwoRegistration_last_name"
                    type="text"
                  />
                </div>
                <div class="col-span-2 mt-5 md:col-span-1">
                  <input-textfield
                    id="stepTwoRegistration_email"
                    v-model="stepTwoRegistrationEmailValue"
                    :disabled="submitPending"
                    :error="!!stepTowRegistrationErrors['stepTwoRegistration_email']"
                    :label="getLabel('email')"
                    :placeholder="getPlaceholder('email')"
                    :read-only="true"
                    :required="true"
                    inverted
                    name="stepTwoRegistration_email"
                    tabindex="-1"
                    type="email"
                  />
                  <InputTextfield
                    id="stepTwoRegistration_password"
                    v-model="stepTwoRegistrationPasswordValue"
                    :disabled="submitPending"
                    :error="!!stepTowRegistrationErrors['stepTwoRegistration_password']"
                    :label="getLabel('password')"
                    :password-toggle="true"
                    :required="true"
                    inverted
                    name="stepTwoRegistration_password"
                    type="password"
                  >
                    <CiPasswordMeter
                      class="absolute right-0 top-[7px]"
                      :text="stepTwoRegistrationPasswordValue"
                    />
                  </InputTextfield>
                </div>
              </form>
            </template>
          </div>
        </template>

        <!-- mandatory -->
        <template
          v-if="step === 1 || (step === 2 && !userExists)"
        >
          <span class="small mt-3"><sup>*</sup><span>{{ $gettext('Mandatory fields') }}</span></span>
        </template>

        <!-- errors -->
        <div
          v-if="error && formSubmitted !== 'stepTwoNoLogin' && formSubmitted !== 'stepTwoLogin'"
          class="mt-12"
          data-cy="reviews-create--error-wrapper-stepOne"
        >
          <CiInfoPanel
            :condition="true"
            type="danger"
          >
            <div v-html="error" />
          </CiInfoPanel>
        </div>

        <!-- cta -->
        <div
          v-if="step === 1"
          class="my-12 grid grid-cols-12 md:gap-x-[30px]"
        >
          <div v-if="i18nDisclaimer" class="order-1 col-span-12 mb-12 w-full px-4 pt-2 text-center text-sm" v-html="i18nDisclaimer" />
          <div class="order-3 col-span-12 mt-4 md:order-2 md:col-span-6 md:mt-0 lg:col-span-3 lg:col-start-4">
            <CiButton
              tag="button"
              variant="text"
              class="w-full"
              :disabled="submitPending"
              @click="cancel"
            >
              {{ i18nCancelText }}
            </CiButton>
          </div>
          <div class="order-2 col-span-12 md:order-3 md:col-span-6 lg:col-span-3">
            <CiButton
              tag="button"
              :disabled="submitPending"
              class="w-full"
              data-cy="reviews-create--button-submit"
              @click="submitStepOne"
            >
              <CiSpinner
                v-if="submitPending"
                class="mr-2"
                type="white"
                size="sm"
              />
              {{ i18nSubmitText }}
            </CiButton>
          </div>
        </div>
        <template v-if="step === 2 && !userExists">
          <div class="mb-4 mt-12">
            <div class="registration-form__passive-acceptance text-center text-sm">
              <p
                v-html="
                  $gettext(
                    'By signing up or creating your account, you agree to our %{termsLink} and confirm that you have read our %{privacyLink}.',
                    { termsLink: i18nTermsOfServiceLink, privacyLink: i18nPrivacyPolicyLink },
                    true,
                  )
                "
              />
            </div>
          </div>
          <div class="mb-12 grid grid-cols-12 md:gap-x-[30px]">
            <div class="order-2 col-span-12 mt-4 md:order-1 md:col-span-6 md:mt-0 lg:col-span-3 lg:col-start-4">
              <CiButton
                tag="button"
                variant="text"
                class="w-full"
                :disabled="submitPending"
                @click="cancel"
              >
                {{ i18nCancelText }}
              </CiButton>
            </div>
            <div class="order-1 col-span-12 md:order-2 md:col-span-6 lg:col-span-3">
              <CiButton
                tag="button"
                :disabled="submitPending"
                class="w-full"
                @click="submitRegistration"
              >
                <CiSpinner
                  v-if="submitPending"
                  class="mr-2"
                  type="white"
                  size="sm"
                />
                {{ i18nSubmitText }}
              </CiButton>
            </div>
          </div>
        </template>
      </div>

      <div
        v-else
        class="container"
      >
        <CiSkeletonReviewsCreate />
      </div>
      <template #placeholder>
        <CiSkeletonReviewsCreate />
      </template>
    </client-only>
  </div>
  <CiErrorView
    v-else
    :error="fetchStatus"
  />
</template>

<style lang="postcss">
.no-touch .reviews {
  .slider-connects {
    @apply cursor-pointer;
  }
}
</style>

<style lang="postcss" scoped>
:deep(.accordion__header) {
  @apply md:border-0;
}

.slider-target,
.slider-target * {
  touch-action: pan-y !important;
}
</style>
